import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiCheckSquare, FiUser, FiLogOut, FiMenu, FiX, FiBell } from 'react-icons/fi';
import TodoPanel from '../todos/TodoPanel';
import TenantSwitcher from './TenantSwitcher';
import LogoPulseCIOwhite from '../../img/pulseCIO_blanc_logo.png';
import LogoPulseCIOblack from '../../img/pulseCIO_black_logo.png';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../utils/customToast';
import { usePlanPermissions, getCurrentPlan } from '../../utils/planPermissions';
import { TenantStatusBadge } from '../shared/TenantStatus';
import { useTutorial } from '../../context/TutorialContext';

// Charge une police Google Fonts dynamiquement (une seule fois par font)
const loadedFonts = new Set();
const loadGoogleFont = (fontName) => {
    if (!fontName || fontName === 'inherit' || loadedFonts.has(fontName)) return;
    const encoded = encodeURIComponent(fontName);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
    loadedFonts.add(fontName);
};

const Header = () => {
    const [todoOpen, setTodoOpen] = useState(false);
    const [todoTargetId, setTodoTargetId] = useState(null);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const { user, logout, activeTenantId, getAuthHeaders } = useAuth();
    const navigate = useNavigate();
    
    const { notifyAction: tutorialNotify } = useTutorial();

    // Permissions basées sur le plan
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { showNotifications, canUseCustomBranding } = usePlanPermissions(currentPlan);
    const mobileMenuRef = useRef(null);
    const userMenuRef = useRef(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const isDarkMode = document.documentElement.classList.contains('dark');

    // Branding personnalisé (Enterprise) - initialisé depuis localStorage pour éviter le flash
    const [branding, setBranding] = useState(() => {
        const tenantId = localStorage.getItem('activeTenantId');
        if (!tenantId) return null;
        try {
            const stored = localStorage.getItem(`branding_${tenantId}`);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });

    // Fermer le menu mobile en cliquant à l'extérieur
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showMobileMenu && mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
                setShowMobileMenu(false);
            }
            if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMobileMenu, showUserMenu]);

    // Charger le branding Enterprise
    useEffect(() => {
        const fetchBranding = async () => {
            if (!activeTenantId || !canUseCustomBranding) {
                setBranding(null);
                localStorage.removeItem(`branding_${activeTenantId}`);
                return;
            }
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/settings/branding`, {
                    headers: getAuthHeaders()
                });
                if (response.ok) {
                    const data = await response.json();
                    const brandingData = data.branding || null;
                    setBranding(brandingData);
                    
                    // Sauvegarder dans localStorage pour éviter le flash au prochain chargement
                    if (brandingData) {
                        localStorage.setItem(`branding_${activeTenantId}`, JSON.stringify(brandingData));
                        if (brandingData.font) loadGoogleFont(brandingData.font);
                    } else {
                        localStorage.removeItem(`branding_${activeTenantId}`);
                    }
                } else {
                    setBranding(null);
                    localStorage.removeItem(`branding_${activeTenantId}`);
                }
            } catch {
                setBranding(null);
                localStorage.removeItem(`branding_${activeTenantId}`);
            }
        };
        fetchBranding();

        const onBrandingUpdate = () => fetchBranding();
        window.addEventListener('brandingUpdated', onBrandingUpdate);
        return () => window.removeEventListener('brandingUpdated', onBrandingUpdate);
    }, [activeTenantId, canUseCustomBranding, getAuthHeaders]);

    // Charger le nombre de notifications non lues
    useEffect(() => {
        const fetchUnreadCount = async () => {
            if (!activeTenantId || !user) return;
            
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/notifications/unread-count`, {
                    headers: getAuthHeaders()
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setUnreadCount(data.count || 0);
                }
            } catch (error) {
                console.error('Error fetching unread count:', error);
            }
        };

        fetchUnreadCount();
        
        // Rafraîchir toutes les 30 secondes
        const interval = setInterval(fetchUnreadCount, 30000);
        
        // Écouter les mises à jour du profil utilisateur
        const handleProfileUpdate = () => {
            setTimeout(() => {
                fetchUnreadCount();
            }, 100);
        };
        
        window.addEventListener('userProfileUpdated', handleProfileUpdate);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('userProfileUpdated', handleProfileUpdate);
        };
    // user?._id au lieu de user : évite un re-fetch quand l'objet user change de référence
    // (ex: updateUser merge) sans que l'identité de l'utilisateur ne change réellement
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id, activeTenantId, getAuthHeaders]);

    const handleLogout = () => {
        setShowUserMenu(false);
        setShowMobileMenu(false);
        toast.success('Déconnexion réussie', { position: 'top-right', autoClose: 3000 });
        logout();
        navigate('/');
    };

    return (
        <header className="bg-white dark:bg-gray-900 text-black dark:text-white shadow-md sticky top-0 z-40">
            <div className="container mx-auto px-4 md:px-1">
                {/* Desktop Header */}
                <div className="hidden lg:flex justify-between items-center py-4">
                    <div className="flex items-center space-x-4">
                        <Link to="/messages" className="flex-shrink-0">
                            {canUseCustomBranding && branding?.name ? (
                                <span
                                    style={{
                                        fontFamily: branding.font || 'inherit',
                                        color: branding.color || undefined,
                                        // Capped at 1.75rem to never make the header grow
                                        fontSize: `${Math.min(2, Math.max(0.9, branding.size ?? 1.25))}rem`,
                                        fontWeight: 700,
                                        letterSpacing: '-0.01em',
                                        lineHeight: 1.2
                                    }}
                                    className="select-none"
                                >
                                    {branding.name}
                                </span>
                            ) : (
                                <img
                                    src={isDarkMode ? LogoPulseCIOwhite : LogoPulseCIOblack}
                                    alt="PulseCIO"
                                    className="h-6 w-auto"
                                />
                            )}
                        </Link>
                        <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-white select-none ai-badge-header"
                            title="Fonctionnalités assistées par IA"
                            style={{background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)', boxShadow: '0 0 8px rgba(139,92,246,0.5)', backgroundSize: '200% 200%'}}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" opacity="0.9" />
                                <path d="M19 16L19.75 19L23 19.75L19.75 20.5L19 23L18.25 20.5L15 19.75L18.25 19L19 16Z" fill="currentColor" opacity="0.7" />
                            </svg>
                            AI
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <TenantSwitcher />
                        <TenantStatusBadge 
                            tenant={user?.tenants?.find(t => t._id === activeTenantId)}
                            onClick={() => navigate('/tenant-settings')}
                        />
                        
                        <button
                            onClick={() => { setTodoOpen(prev => { if (!prev) tutorialNotify('todo-opened'); return !prev; }); }}
                            className="flex items-center gap-2 bg-primary text-white dark:text-black px-4 py-2 rounded-lg shadow-md hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition"
                            title="Ouvrir TODO"
                            aria-label="Ouvrir TODO"
                            aria-expanded={todoOpen}
                            data-tutorial="todo-btn"
                        >
                            <FiCheckSquare className="h-5 w-5" aria-hidden="true" />
                            <span className="text-sm font-semibold">TODO</span>
                        </button>

                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={() => setShowUserMenu(prev => !prev)}
                                data-tutorial="user-menu-btn"
                                className="relative flex items-center gap-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 px-4 py-2 rounded-lg transition"
                                aria-label="Menu utilisateur"
                            >
                                {user?.avatar ? (
                                    <img src={user.avatar} alt={user.name} className="h-5 w-5 rounded-full object-cover" />
                                ) : (
                                    <FiUser className="h-5 w-5" aria-hidden="true" />
                                )}
                                <span className="text-sm font-semibold">{user?.name}</span>
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </button>

                            {showUserMenu && (
                                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 pb-2 z-50">
                                    <div className="px-4 py-3 border-b dark:border-gray-700">
                                        <p className="text-sm font-semibold text-black dark:text-white truncate">{user?.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            navigate('/profile');
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition"
                                    >
                                        <FiUser className="h-4 w-4" aria-hidden="true" />
                                        Mon profil
                                    </button>
                                    {showNotifications && (
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                navigate('/notifications');
                                            }}
                                            className="relative w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition"
                                        >
                                            <FiBell className="h-4 w-4" aria-hidden="true" />
                                            <span>Notifications</span>
                                            {unreadCount > 0 && (
                                                <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                                    {unreadCount > 99 ? '99+' : unreadCount}
                                                </span>
                                            )}
                                        </button>
                                    )}
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-red-600 dark:text-red-400 transition"
                                    >
                                        <FiLogOut className="h-4 w-4" aria-hidden="true" />
                                        Déconnexion
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile Header */}
                <div className="lg:hidden flex justify-between items-center py-3">
                    <h1 className="text-lg font-bold truncate max-w-[60%]">
                        <Link to="/" className="flex items-center hover:underline">
                            {canUseCustomBranding && branding?.name ? (
                                <span
                                    style={{
                                        fontFamily: branding.font || 'inherit',
                                        color: branding.color || undefined,
                                        fontSize: `${Math.min(2, Math.max(0.9, branding.size ?? 1.25))}rem`,
                                        fontWeight: 700
                                    }}
                                    className="ml-3 select-none"
                                >
                                    {branding.name}
                                </span>
                            ) : (
                                <>
                                    <img src={LogoPulseCIOblack} alt="PulseCIO logo" className="ml-3 h-6 w-auto block dark:hidden" loading="lazy" />
                                    <img src={LogoPulseCIOwhite} alt="PulseCIO logo" className="ml-3 h-6 w-auto hidden dark:block" loading="lazy" />
                                </>
                            )}
                        </Link>
                    </h1>
                    
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setTodoOpen(prev => !prev)}
                            className="bg-primary text-white dark:text-black p-2.5 rounded-lg shadow-md hover:bg-primary-hover transition"
                            title="TODO"
                            aria-label="Ouvrir TODO"
                        >
                            <FiCheckSquare className="h-5 w-5" aria-hidden="true" />
                        </button>

                        <button
                            onClick={() => setShowMobileMenu(prev => !prev)}
                            className="relative bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 p-2.5 rounded-lg transition"
                            aria-label="Menu"
                        >
                            {showMobileMenu ? <FiX className="h-5 w-5" aria-hidden="true" /> : <FiMenu className="h-5 w-5" aria-hidden="true" />}
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu Dropdown avec animation */}
                <div 
                    ref={mobileMenuRef}
                    className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
                        showMobileMenu ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                    <div className="border-t dark:border-gray-700 py-4 space-y-3">
                        {/* User Info Card */}
                        <div className="px-4 py-3 bg-gradient-to-r from-primary/5 to-accent/5 dark:from-primary/10 dark:to-accent/10 rounded-lg border border-primary/20 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                {user?.avatar ? (
                                    <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-primary text-white dark:text-black flex items-center justify-center font-bold text-lg">
                                        {user?.name?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-black dark:text-white truncate">{user?.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                                </div>
                            </div>
                        </div>

                        {/* Tenant Switcher */}
                        <div className="md:px-4 px-0">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 px-1">
                                Organisation
                            </div>
                            <TenantSwitcher />
                        </div>

                        {/* Menu Items */}
                        <div className="space-y-1 md:px-4 px-0">
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 px-1">
                                Menu
                            </div>
                            
                            <button
                                onClick={() => {
                                    setShowMobileMenu(false);
                                    navigate('/profile');
                                }}
                                className="w-full flex items-center gap-3 py-3 lg:px-0 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition group"
                            >
                                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:bg-primary group-hover:text-white dark:group-hover:text-black transition">
                                    <FiUser className="h-4 w-4" aria-hidden="true" />
                                </div>
                                <div className="flex-1 text-left">
                                    <p className="font-medium text-sm text-black dark:text-white">Mon profil</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Gérer mon compte</p>
                                </div>
                            </button>

                            {showNotifications && (
                                <button
                                    onClick={() => {
                                        setShowMobileMenu(false);
                                        navigate('/notifications');
                                    }}
                                    className="relative w-full flex items-center gap-3 py-3 lg:px-0 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition group"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center group-hover:bg-primary group-hover:text-white dark:group-hover:text-black transition">
                                        <FiBell className="h-4 w-4" aria-hidden="true" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="font-medium text-sm text-black dark:text-white">Notifications</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Vos mentions et alertes</p>
                                    </div>
                                    {unreadCount > 0 && (
                                        <span className="bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                                            {unreadCount > 99 ? '99+' : unreadCount}
                                        </span>
                                    )}
                                </button>
                            )}

                            <button
                                onClick={() => {
                                    setShowMobileMenu(false);
                                    handleLogout();
                                }}
                                className="w-full flex items-center gap-3 py-3 lg:px-0 px-3 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition group"
                            >
                                <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition">
                                    <FiLogOut className="h-4 w-4 text-red-600 dark:text-red-400 group-hover:text-white" aria-hidden="true" />
                                </div>
                                <div className="flex-1 text-left">
                                    <p className="font-medium text-sm text-red-600 dark:text-red-400">Déconnexion</p>
                                    <p className="text-xs text-red-500 dark:text-red-500">Quitter l'application</p>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <TodoPanel
                open={todoOpen}
                onClose={() => { 
                    setTodoOpen(false); 
                    setTodoTargetId(null); 
                }}
                highlightId={todoTargetId}
                key={todoTargetId || 'default'}
            />
        </header>
    );
};

export default Header;